(() => {
  'use strict';
  document.documentElement.classList.add('js');
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const coarse = window.matchMedia('(pointer: coarse)');
  const theme = window.FloodTheme || {};
  const routes = theme.routes || {};
  const q = (selector, root = document) => root.querySelector(selector);
  const qa = (selector, root = document) => Array.from(root.querySelectorAll(selector));
  const wait = (fn, ms = 180) => { let timer; return (...args) => { clearTimeout(timer); timer = setTimeout(() => fn(...args), ms); }; };
  const escapeHTML = value => String(value ?? '').replace(/[&<>'"]/g, char => ({'&':'&amp;','<':'&lt;','>':'&gt;',"'":'&#39;','"':'&quot;'}[char]));
  const money = cents => { try { return new Intl.NumberFormat(theme.locale || undefined, { style:'currency', currency: theme.currency || 'USD' }).format((Number(cents) || 0) / 100); } catch (_) { return `$${((Number(cents) || 0) / 100).toFixed(2)}`; } };

  function stickyHeaderState() {
    if (document.documentElement.dataset.floodHeaderScrollInitialized) return;
    document.documentElement.dataset.floodHeaderScrollInitialized = 'true';
    const update = () => document.body.classList.toggle('is-header-scrolled', window.scrollY > 18);
    update();
    window.addEventListener('scroll', update, { passive:true });
  }

  function mobileMenu(root = document) {
    qa('[data-menu-toggle]', root).forEach(toggle => {
      if (toggle.dataset.initialized) return;
      toggle.dataset.initialized = 'true';
      const nav = document.getElementById(toggle.getAttribute('aria-controls'));
      if (!nav) return;
      let previousFocus = null;
      const close = (restore = false) => {
        document.body.classList.remove('is-menu-open');
        toggle.setAttribute('aria-expanded', 'false');
        toggle.textContent = 'Menu';
        nav.setAttribute('aria-hidden', 'true');
        if (restore && previousFocus) previousFocus.focus();
      };
      const open = () => {
        previousFocus = document.activeElement;
        document.body.classList.add('is-menu-open');
        toggle.setAttribute('aria-expanded', 'true');
        toggle.textContent = 'Close';
        nav.setAttribute('aria-hidden', 'false');
        toggle.focus();
      };
      toggle.addEventListener('click', () => document.body.classList.contains('is-menu-open') ? close(true) : open());
      qa('a', nav).forEach(link => link.addEventListener('click', () => close(false)));
      document.addEventListener('keydown', event => {
        if (event.key === 'Escape' && document.body.classList.contains('is-menu-open')) close(true);
        if (event.key !== 'Tab' || !document.body.classList.contains('is-menu-open')) return;
        const focusable = [toggle, ...qa('a,button,[tabindex]:not([tabindex="-1"])', nav)].filter(el => !el.disabled);
        if (!focusable.length) return;
        const first = focusable[0], last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
      });
    });
  }

  function hero(root) {
    if (root.dataset.initialized) return;
    root.dataset.initialized = 'true';
    const slides = qa('[data-slide]', root);
    if (!slides.length) return;
    const dots = qa('[data-dot]', root), current = q('[data-current]', root), total = q('[data-total]', root), pause = q('[data-pause]', root);
    const interval = Math.max(5000, Number(root.dataset.interval || 8000));
    const autoplay = root.dataset.autoplay !== 'false';
    root.style.setProperty('--f-interval', `${interval}ms`);
    let index = 0, timer = null, userPaused = !autoplay, hoverPaused = false, focusPaused = false, touchX = null, touchY = null;
    if (total) total.textContent = String(slides.length).padStart(2, '0');
    const render = number => {
      index = (number + slides.length) % slides.length;
      slides.forEach((slide, i) => {
        const active = i === index;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
        if ('inert' in slide) slide.inert = !active;
        qa('a,button', slide).forEach(el => active ? el.removeAttribute('tabindex') : el.setAttribute('tabindex', '-1'));
      });
      dots.forEach((dot, i) => { dot.classList.toggle('is-active', i === index); dot.setAttribute('aria-current', i === index ? 'true' : 'false'); });
      if (current) current.textContent = String(index + 1).padStart(2, '0');
      root.classList.remove('is-running');
      void root.offsetWidth;
      if (!userPaused && !reduced.matches) root.classList.add('is-running');
    };
    const pauseVideos = () => qa('.f-hero__video', root).forEach(video => { video.pause(); video.closest('[data-slide]')?.classList.remove('is-video-ready'); });
    const playActiveVideo = () => {
      const slide = slides[index], video = q('.f-hero__video', slide);
      if (!video || reduced.matches || userPaused || document.hidden) return;
      const markReady = () => slide.classList.add('is-video-ready');
      if (video.readyState >= 2) markReady(); else video.addEventListener('canplay', markReady, { once:true });
      video.play().then(markReady).catch(() => {});
    };
    const stop = () => { window.clearInterval(timer); timer = null; root.classList.remove('is-running'); pauseVideos(); };
    const start = () => { stop(); if (userPaused || hoverPaused || focusPaused || reduced.matches || document.hidden) return; playActiveVideo(); if (slides.length < 2) return; root.classList.add('is-running'); timer = window.setInterval(() => { render(index + 1); pauseVideos(); playActiveVideo(); }, interval); };
    const move = amount => { render(index + amount); start(); };
    q('[data-prev]', root)?.addEventListener('click', () => move(-1));
    q('[data-next]', root)?.addEventListener('click', () => move(1));
    dots.forEach((dot, i) => dot.addEventListener('click', () => { render(i); start(); }));
    const updatePause = () => {
      if (!pause) return;
      pause.setAttribute('aria-pressed', String(userPaused));
      pause.setAttribute('aria-label', userPaused ? 'Resume slideshow' : 'Pause slideshow');
      pause.innerHTML = userPaused ? '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 5v14l11-7z" fill="currentColor"/></svg>' : '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 5v14M16 5v14" fill="none" stroke="currentColor" stroke-width="2"/></svg>';
    };
    pause?.addEventListener('click', () => {
      userPaused = !userPaused;
      updatePause();
      userPaused ? stop() : start();
    });
    root.addEventListener('mouseenter', () => { hoverPaused = true; stop(); });
    root.addEventListener('mouseleave', () => { hoverPaused = false; start(); });
    root.addEventListener('focusin', () => { focusPaused = true; stop(); });
    root.addEventListener('focusout', event => { if (!root.contains(event.relatedTarget)) { focusPaused = false; start(); } });
    root.addEventListener('keydown', event => { if (event.key === 'ArrowLeft') { event.preventDefault(); move(-1); } if (event.key === 'ArrowRight') { event.preventDefault(); move(1); } });
    root.addEventListener('touchstart', event => { touchX = event.changedTouches[0].clientX; touchY = event.changedTouches[0].clientY; stop(); }, { passive:true });
    root.addEventListener('touchend', event => { if (touchX === null) return; const dx = event.changedTouches[0].clientX - touchX, dy = event.changedTouches[0].clientY - touchY; if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) render(index + (dx < 0 ? 1 : -1)); touchX = touchY = null; start(); }, { passive:true });
    document.addEventListener('visibilitychange', () => document.hidden ? stop() : start());
    reduced.addEventListener?.('change', () => { render(index); updatePause(); start(); });
    updatePause(); render(0); start();
  }

  function rails(root = document) {
    qa('[data-rail]', root).forEach(wrap => {
      if (wrap.dataset.initialized) return;
      wrap.dataset.initialized = 'true';
      const rail = q('[data-rail-track]', wrap);
      if (!rail) return;
      qa('[data-rail-prev]', wrap).forEach(button => button.addEventListener('click', () => rail.scrollBy({ left:-rail.clientWidth * .8, behavior: reduced.matches ? 'auto' : 'smooth' })));
      qa('[data-rail-next]', wrap).forEach(button => button.addEventListener('click', () => rail.scrollBy({ left:rail.clientWidth * .8, behavior: reduced.matches ? 'auto' : 'smooth' })));
    });
  }

  function reveal(root = document) {
    const elements = qa('.f-reveal:not(.is-visible)', root);
    if (reduced.matches || !('IntersectionObserver' in window)) { elements.forEach(el => el.classList.add('is-visible')); return; }
    const observer = new IntersectionObserver(entries => entries.forEach(entry => { if (entry.isIntersecting) { entry.target.classList.add('is-visible'); observer.unobserve(entry.target); } }), { rootMargin:'0px 0px -8%' });
    elements.forEach(el => observer.observe(el));
  }

  function previewVideos(root = document) {
    qa('[data-preview-card]', root).forEach(card => {
      if (card.dataset.videoInitialized) return;
      card.dataset.videoInitialized = 'true';
      const video = q('.f-card__preview-video', card);
      if (!video || coarse.matches || reduced.matches) return;
      const play = () => { card.classList.add('is-previewing'); video.play().catch(() => {}); };
      const stop = () => { card.classList.remove('is-previewing'); video.pause(); try { video.currentTime = 0; } catch (_) {} };
      card.addEventListener('pointerenter', play);
      card.addEventListener('pointerleave', stop);
      card.addEventListener('focusin', play);
      card.addEventListener('focusout', event => { if (!card.contains(event.relatedTarget)) stop(); });
    });
  }

  function dialogs(root = document) {
    qa('[data-dialog-close]', root).forEach(button => {
      if (button.dataset.initialized) return;
      button.dataset.initialized = 'true';
      button.addEventListener('click', () => button.closest('dialog')?.close());
    });
    qa('dialog.f-dialog', root).forEach(dialog => {
      if (dialog.dataset.backdropInitialized) return;
      dialog.dataset.backdropInitialized = 'true';
      dialog.addEventListener('click', event => { if (event.target === dialog) dialog.close(); });
    });
    qa('[data-search-open]', root).forEach(opener => {
      if (opener.dataset.initialized) return;
      opener.dataset.initialized = 'true';
      opener.addEventListener('click', event => { const dialog = q('[data-search-dialog]'); if (!dialog?.showModal) return; event.preventDefault(); dialog.showModal(); requestAnimationFrame(() => q('[data-search-input]', dialog)?.focus()); });
    });
  }

  function predictiveSearch() {
    const dialog = q('[data-search-dialog]');
    if (!dialog || dialog.dataset.searchInitialized) return;
    dialog.dataset.searchInitialized = 'true';
    const input = q('[data-search-input]', dialog), results = q('[data-search-results]', dialog);
    if (!input || !results || !routes.predictiveSearch) return;
    const search = wait(async () => {
      const term = input.value.trim();
      if (term.length < 2) { results.innerHTML = '<p class="f-muted">Type at least two characters.</p>'; return; }
      results.innerHTML = '<p class="f-muted">Searching the world...</p>';
      try {
        const url = `${routes.predictiveSearch}.json?q=${encodeURIComponent(term)}&resources[type]=product,page,article&resources[limit]=6&resources[options][unavailable_products]=last`;
        const response = await fetch(url, { headers:{ Accept:'application/json' } });
        if (!response.ok) throw new Error('Search unavailable');
        const data = await response.json();
        const groups = data.resources?.results || {};
        const items = [...(groups.products || []), ...(groups.pages || []), ...(groups.articles || [])];
        if (!items.length) { results.innerHTML = '<p class="f-serif">No results. Try another current.</p>'; return; }
        results.innerHTML = `<div class="f-search-results">${items.map(item => `<a href="${escapeHTML(item.url)}"><span>${escapeHTML(item.title)}</span><small>${escapeHTML(item.type || item.vendor || 'FLOOD')}</small><b aria-hidden="true">→</b></a>`).join('')}</div>`;
      } catch (_) { results.innerHTML = '<p class="f-muted">Use the Search button to continue.</p>'; }
    }, 180);
    input.addEventListener('input', search);
  }

  async function fetchCart() {
    const response = await fetch(`${routes.cart}.js`, { headers:{ Accept:'application/json' }, credentials:'same-origin' });
    if (!response.ok) throw new Error('Cart unavailable');
    return response.json();
  }

  function renderCart(cart) {
    qa('[data-cart-count]').forEach(el => { el.textContent = cart.item_count; el.setAttribute('aria-label', `${cart.item_count} items`); });
    const drawer = q('[data-cart-drawer]');
    if (!drawer) return;
    const items = q('[data-cart-items]', drawer), footer = q('[data-cart-footer]', drawer), subtotal = q('[data-cart-subtotal]', drawer);
    if (!items) return;
    if (!cart.items?.length) {
      items.innerHTML = `<div class="f-cart-drawer__empty"><p class="f-serif">Nothing here yet.</p><a class="f-button f-button--solid" href="${escapeHTML(routes.root || '/')}collections/all">Enter Market</a></div>`;
      if (footer) footer.hidden = true;
      return;
    }
    items.innerHTML = cart.items.map((item, index) => {
      const image = item.featured_image?.url || item.image || '';
      const image240 = image ? `${image}${image.includes('?') ? '&' : '?'}width=240` : '';
      const variant = item.variant_title && item.variant_title !== 'Default Title' ? `<p>${escapeHTML(item.variant_title)}</p>` : '';
      return `<article class="f-cart-drawer__item"><a href="${escapeHTML(item.url)}" class="f-cart-drawer__image">${image240 ? `<img src="${escapeHTML(image240)}" alt="${escapeHTML(item.product_title)}" width="120" height="120" loading="lazy">` : ''}</a><div><h3><a href="${escapeHTML(item.url)}">${escapeHTML(item.product_title)}</a></h3>${variant}<div class="f-quantity"><button type="button" data-cart-change data-line="${index + 1}" data-quantity="${Math.max(0, item.quantity - 1)}" aria-label="Decrease ${escapeHTML(item.product_title)} quantity">−</button><span>${item.quantity}</span><button type="button" data-cart-change data-line="${index + 1}" data-quantity="${item.quantity + 1}" aria-label="Increase ${escapeHTML(item.product_title)} quantity">+</button></div></div><strong>${money(item.final_line_price)}</strong></article>`;
    }).join('');
    if (subtotal) subtotal.textContent = money(cart.total_price);
    if (footer) footer.hidden = false;
  }

  function cartDrawer() {
    const drawer = q('[data-cart-drawer]');
    if (!drawer || drawer.dataset.cartInitialized) return;
    drawer.dataset.cartInitialized = 'true';
    const status = q('[data-cart-status]', drawer);
    const setStatus = text => { if (status) status.textContent = text; };
    const refresh = async () => { try { const cart = await fetchCart(); renderCart(cart); return cart; } catch (_) { setStatus('Cart could not refresh. Open the full cart to continue.'); return null; } };
    document.addEventListener('click', async event => {
      const opener = event.target.closest('[data-cart-open]');
      if (opener && drawer.showModal) { event.preventDefault(); await refresh(); drawer.showModal(); q('[data-dialog-close]', drawer)?.focus(); return; }
      const change = event.target.closest('[data-cart-change]');
      if (!change) return;
      change.disabled = true; setStatus('Updating cart...');
      try {
        const response = await fetch(`${routes.cartChange}.js`, { method:'POST', headers:{ 'Content-Type':'application/json', Accept:'application/json' }, credentials:'same-origin', body:JSON.stringify({ line:Number(change.dataset.line), quantity:Number(change.dataset.quantity) }) });
        if (!response.ok) throw new Error('Update failed');
        renderCart(await response.json()); setStatus('Cart updated.');
      } catch (_) { setStatus('Cart could not update. Open the full cart to continue.'); }
    });
    document.addEventListener('submit', async event => {
      const form = event.target.closest('form[action*="/cart/add"]');
      if (!form || !drawer.showModal || (event.submitter && event.submitter.name && event.submitter.name !== 'add')) return;
      event.preventDefault();
      const button = event.submitter || q('[name="add"]', form);
      const original = button?.textContent;
      if (button) { button.disabled = true; button.textContent = 'Adding...'; }
      try {
        const response = await fetch(`${routes.cartAdd}.js`, { method:'POST', body:new FormData(form), headers:{ Accept:'application/json' }, credentials:'same-origin' });
        if (!response.ok) { const error = await response.json().catch(() => ({})); throw new Error(error.description || 'Could not add to cart'); }
        const cart = await fetchCart(); renderCart(cart); setStatus('Added to your cart.'); drawer.showModal();
      } catch (error) { setStatus(error.message || 'Could not add to cart.'); drawer.showModal(); }
      finally { if (button) { button.disabled = false; button.textContent = original || 'Add to cart'; } }
    });
  }

  function productRecommendations(root = document) {
    qa('[data-product-recommendations]', root).forEach(element => {
      if (element.dataset.initialized || element.innerHTML.trim() || !element.dataset.url) return;
      element.dataset.initialized = 'true';
      fetch(element.dataset.url).then(response => response.text()).then(text => {
        const parsed = new DOMParser().parseFromString(text, 'text/html');
        const fresh = q('[data-product-recommendations]', parsed);
        if (fresh?.innerHTML.trim()) { element.innerHTML = fresh.innerHTML; reveal(element); }
      }).catch(() => {});
    });
  }

  function products(root = document) {
    qa('[data-product-root]', root).forEach(productRoot => {
      if (productRoot.dataset.initialized) return;
      productRoot.dataset.initialized = 'true';
      const select = q('[data-variant-select]', productRoot);
      const currentPrice = q('[data-product-current-price]', productRoot);
      const comparePrice = q('[data-product-compare-price]', productRoot);
      const add = q('[data-add-to-cart]', productRoot);
      const status = q('[data-product-status]', productRoot);
      const json = q('[data-product-json]', productRoot);
      const groups = qa('[data-product-option]', productRoot);
      const gallery = q('[data-product-gallery]', productRoot);
      if (!select || !json) return;
      let variants = [];
      try { variants = JSON.parse(json.textContent); } catch (_) { return; }

      const selectedValues = () => groups.map(group => q('input:checked', group)?.value || '');
      const findVariant = values => variants.find(variant => variant.options?.every((option, index) => option === values[index]));
      const syncRadios = variant => {
        if (!variant?.options) return;
        groups.forEach((group, index) => {
          const radio = qa('input[type="radio"]', group).find(input => input.value === variant.options[index]);
          if (radio) radio.checked = true;
        });
      };
      const updateOptionAvailability = () => {
        const selected = selectedValues();
        groups.forEach((group, groupIndex) => {
          qa('input[type="radio"]', group).forEach(input => {
            const candidate = [...selected]; candidate[groupIndex] = input.value;
            const possible = variants.some(variant => variant.available && variant.options?.every((value, index) => value === candidate[index]));
            input.disabled = !possible;
            const label = productRoot.querySelector(`label[for="${CSS.escape(input.id)}"]`);
            label?.classList.toggle('is-unavailable', !possible);
            label?.setAttribute('aria-disabled', String(!possible));
          });
        });
      };
      const render = (variant, userInitiated = false) => {
        if (!variant) return;
        select.value = String(variant.id);
        if (currentPrice) currentPrice.textContent = variant.price_formatted;
        if (comparePrice) {
          const sale = Number(variant.compare_at_price) > Number(variant.price);
          comparePrice.hidden = !sale;
          comparePrice.textContent = sale ? variant.compare_at_price_formatted : '';
        }
        if (add) {
          add.disabled = !variant.available;
          add.textContent = variant.available ? 'Add to cart' : 'Sold out';
          add.classList.toggle('f-product__sold', !variant.available);
        }
        if (status) {
          status.hidden = variant.available;
          status.textContent = variant.available ? '' : 'This option is currently sold out.';
        }
        qa('[data-media-id]', gallery || productRoot).forEach(media => media.classList.toggle('is-variant-media', Number(media.dataset.mediaId) === Number(variant.featured_media_id)));
        if (userInitiated && gallery && variant.featured_media_id && window.matchMedia('(max-width: 820px)').matches) {
          const media = q(`[data-media-id="${variant.featured_media_id}"]`, gallery);
          if (media) gallery.scrollTo({ left:media.offsetLeft, behavior:reduced.matches ? 'auto' : 'smooth' });
        }
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
        updateOptionAvailability();
      };

      groups.forEach(group => group.addEventListener('change', () => {
        const variant = findVariant(selectedValues());
        if (variant) render(variant, true);
      }));
      select.addEventListener('change', () => {
        const variant = variants.find(item => String(item.id) === select.value);
        if (!variant) return;
        syncRadios(variant); render(variant, true);
      });

      const initial = variants.find(item => String(item.id) === select.value) || findVariant(selectedValues()) || variants[0];
      if (initial) { syncRadios(initial); render(initial); }
    });
  }

  function addressSelectors(root = document) {
    qa('[data-address-country]', root).forEach(country => {
      if (country.dataset.initialized) return;
      country.dataset.initialized = 'true';
      const form = country.closest('form') || country.parentElement;
      const province = q('[data-address-province]', form);
      if (!province) return;
      const countryDefault = country.dataset.default;
      if (countryDefault) {
        const match = Array.from(country.options).find(option => option.value === countryDefault || option.text === countryDefault);
        if (match) country.value = match.value;
      }
      const update = () => {
        const option = country.options[country.selectedIndex];
        let values = [];
        try { values = JSON.parse(option?.dataset.provinces || '[]'); } catch (_) { values = []; }
        province.innerHTML = '';
        values.forEach(pair => {
          const item = document.createElement('option');
          item.value = pair[0]; item.textContent = pair[1]; province.appendChild(item);
        });
        const provinceDefault = province.dataset.default;
        if (provinceDefault) {
          const match = Array.from(province.options).find(item => item.value === provinceDefault || item.text === provinceDefault);
          if (match) province.value = match.value;
        }
        const label = province.closest('label');
        if (label) label.hidden = values.length === 0;
      };
      country.addEventListener('change', update);
      update();
    });
  }

  function placeholderLinks(root = document) { qa('a[href="#"]', root).forEach(link => { if (!link.dataset.placeholder) { link.dataset.placeholder = 'true'; link.addEventListener('click', event => event.preventDefault()); } }); }

  function boot(root = document) {
    stickyHeaderState();
    mobileMenu(root); qa('[data-hero]', root).forEach(hero); rails(root); reveal(root); previewVideos(root); dialogs(root); products(root); productRecommendations(root); addressSelectors(root); placeholderLinks(root);
    predictiveSearch(); cartDrawer();
  }

  document.addEventListener('shopify:section:load', event => boot(event.target));
  document.addEventListener('shopify:block:select', event => { const slide = event.target.closest?.('[data-slide]'), heroRoot = event.target.closest?.('[data-hero]'); if (slide && heroRoot) { const index = qa('[data-slide]', heroRoot).indexOf(slide); qa('[data-dot]', heroRoot)[index]?.click(); } });
  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', () => boot()) : boot();
})();
